import {
  useLocationPathname,
  useLocationState,
  useNavigate,
  useParams,
} from '@rocketlane/rocket-router';
import { PageLoader } from '@rocketlane/ui';
import React, { Suspense, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import styled from 'styled-components';
import { useWithAccess } from 'hooks/rbac/useWithAccess';
import { useAgentBuilderHostAPI } from 'hooks/use-agent-builder-host-api';
import { useTranslation } from 'hooks/use-translation';
import { useAccountDataQuery } from 'queries/account';
import { NotFound } from 'routes/404';

// Lazy load the federated component from signals remote
const DocumentAgentPreview = React.lazy(
  () => import('signals/DocumentAgentPreview'),
);

const StyledDocumentAgentPreviewPage = styled.div`
  background-color: var(--scarlet-gray-80);
`;

/**
 * Federated DocumentAgentPreviewPage component that can be consumed by the parent application
 * This component loads DocumentAgentPreview from the signals remote (rocket-ai-frontend)
 * It extracts documentationAgentId from URL params and passes it via location state
 */
export const DocumentAgentPreviewPage: React.FC = () => {
  const { documentationAgentId } = useParams<{
    documentationAgentId: string;
  }>();
  const pathname = useLocationPathname();
  const locState = useLocationState() as {
    documentationAgentId?: number;
    _hasSetState?: boolean;
  } | null;
  const navigate = useNavigate();
  const tabTitle = useTranslation('document_agent.preview.tab_title');
  const { hasAccessToResource } = useWithAccess();
  const documentAgentPermissions = useMemo(
    () => ({
      read: hasAccessToResource({
        rule: 'DOCUMENT_AGENT:READ',
      }),
      write: hasAccessToResource({
        rule: 'DOCUMENT_AGENT:WRITE',
      }),
      delete: hasAccessToResource({
        rule: 'DOCUMENT_AGENT:DELETE',
      }),
      create: hasAccessToResource({
        rule: 'DOCUMENT_AGENT:CREATE',
      }),
      invoke: hasAccessToResource({
        rule: 'DOCUMENT_AGENT:CREATE',
      }),
    }),
    [hasAccessToResource],
  );

  // Ensure documentationAgentId is in location state for the remote component
  // Always sync URL param to state, ensuring URL param takes precedence over stale state
  useEffect(() => {
    if (documentationAgentId) {
      const urlParamId = parseInt(documentationAgentId, 10);

      // Validate that the parsed ID is a valid number
      if (!Number.isNaN(urlParamId) && Number.isFinite(urlParamId)) {
        const stateId = locState?.documentationAgentId;

        // Update state if: no state ID exists, state ID doesn't match URL param, or state hasn't been set
        if (!stateId || stateId !== urlParamId || !locState?._hasSetState) {
          navigate(pathname, {
            state: {
              ...locState,
              documentationAgentId: urlParamId,
              _hasSetState: true,
            },
            replace: true,
          });
        }
      }
      // If invalid ID, don't update state - let the remote component handle the error
    }
  }, [documentationAgentId, pathname, locState, navigate]);

  const hostAPI = useAgentBuilderHostAPI();

  return (
    <StyledDocumentAgentPreviewPage className="document-agent-preview-page w-full h-full">
      <Helmet>
        <title>{tabTitle}</title>
      </Helmet>
      <Suspense fallback={<PageLoader />}>
        <DocumentAgentPreview
          hostAPI={
            hostAPI as {
              renderComponent: (request: unknown) => React.ReactNode;
            }
          }
          documentAgentPermissions={documentAgentPermissions}
          hasAccessToResource={hasAccessToResource}
        />
      </Suspense>
    </StyledDocumentAgentPreviewPage>
  );
};

/**
 * Renders DocumentAgentPreviewPage only when documentationAgent feature is enabled; otherwise NotFound.
 */
export const DocumentAgentPreviewRouteGuard: React.FC = () => {
  const { data: accountData, isLoading } = useAccountDataQuery();
  const isDocumentationAgentEnabled =
    accountData?.accountMeta?.features?.documentationAgent ?? false;

  if (isLoading) {
    return <PageLoader />;
  }
  if (!isDocumentationAgentEnabled) {
    return <NotFound />;
  }

  return <DocumentAgentPreviewPage />;
};
